/**
 * The box the arm's wrist camera has mapped, as the arm sends it.
 *
 * The arm keeps a 5 mm voxel map of whatever the camera has seen (pick_place.perception.box_map)
 * and publishes the occupied voxels every couple of seconds while it grows, every ten while it
 * does not. A voxel travels as its flat index on the arm's lattice, and the sorted indices as
 * the gaps between them, deflated: walls are runs of neighbouring indices, so forty thousand
 * voxels are a few tens of kB. The camera mount's own surface comes along, in the tool's frame,
 * for drawing where it is from the status's pose.
 */
import { json, useSubscription } from "adamo-react";
import { useRef, useState } from "react";

import { boxmapTopic } from "./topics";

export type BoxMapMessage = {
  type: "boxmap";
  lo: [number, number, number];     // m, the lattice's corner in the arm's base frame
  voxel: number;                    // m
  shape: [number, number, number];
  n: number;
  frames: number;                   // how many camera frames it has taken in so far
  idx: string;                      // base64 of zlib(uint32le gaps between sorted flat indices)
  mount: string;                    // base64 of int16le x y z triples, mm, tool frame
};

/** The gripper end of the arm, sent now and then on the same topic: meshes in their bodies' frames. */
export type ToolModelMessage = {
  type: "toolmodel";
  parts: { body: string; role: "arm" | "finger" | "camera"; v: string; f: string }[];  // int16le mm / uint16le
};

export type ToolPart = { body: string; role: "arm" | "finger" | "camera"; positions: Float32Array; index: Uint16Array };
export type ToolModel = { parts: ToolPart[] };

export function decodeToolModel(msg: ToolModelMessage): ToolModel {
  return {
    parts: msg.parts.map((p) => {
      const mm = new Int16Array(bytes(p.v).buffer);
      const positions = new Float32Array(mm.length);
      for (let i = 0; i < mm.length; i += 1) positions[i] = mm[i] / 1000;
      return { body: p.body, role: p.role, positions, index: new Uint16Array(bytes(p.f).buffer) };
    }),
  };
}

export type BoxMap = {
  /** Voxel centres, metres, the arm's base frame (z up). */
  positions: Float32Array;
  n: number;
  voxel: number;
  frames: number;
  /** The camera mount's surface points, metres, the tool frame. */
  mount: Float32Array;
  receivedAt: number;               // performance.now()
};

function bytes(b64: string): Uint8Array {
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

async function inflate(data: Uint8Array): Promise<ArrayBuffer> {
  // "deflate" in the Compression Streams API is the zlib format, which is what Python's zlib writes.
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new DecompressionStream("deflate"));
  return new Response(stream).arrayBuffer();
}

export async function decodeBoxMap(msg: BoxMapMessage): Promise<BoxMap> {
  const gaps = new Uint32Array(await inflate(bytes(msg.idx)));
  const [nx, ny, nz] = msg.shape;
  const [x0, y0, z0] = msg.lo;
  const v = msg.voxel;
  const positions = new Float32Array(gaps.length * 3);
  let flat = 0;
  for (let n = 0; n < gaps.length; n += 1) {
    flat += gaps[n];
    const k = flat % nz;
    const j = Math.floor(flat / nz) % ny;
    const i = Math.floor(flat / (nz * ny));
    positions[3 * n] = x0 + (i + 0.5) * v;
    positions[3 * n + 1] = y0 + (j + 0.5) * v;
    positions[3 * n + 2] = z0 + (k + 0.5) * v;
  }
  void nx;
  const mm = new Int16Array(bytes(msg.mount).buffer);
  const mount = new Float32Array(mm.length);
  for (let i = 0; i < mm.length; i += 1) mount[i] = mm[i] / 1000;
  return { positions, n: gaps.length, voxel: v, frames: msg.frames, mount, receivedAt: performance.now() };
}

/** The newest map and gripper model, as React state for the page and as refs for the headset's render loop. */
export function useBoxMap(org: string | null, robot: string) {
  const latest = useRef<BoxMap | null>(null);
  const [map, setMap] = useState<BoxMap | null>(null);
  const toolLatest = useRef<ToolModel | null>(null);
  const [tool, setTool] = useState<ToolModel | null>(null);
  const decoding = useRef(false);
  useSubscription(org ? boxmapTopic(org, robot) : "", (sample) => {
    const msg = json.decode<BoxMapMessage | ToolModelMessage>(sample.payload().toBytes());
    if (msg.type === "toolmodel") {
      if (!toolLatest.current) {           // it never changes: the first one is the one
        toolLatest.current = decodeToolModel(msg);
        setTool(toolLatest.current);
      }
      return;
    }
    if (decoding.current) return;         // one at a time; the next one is along in two seconds
    decoding.current = true;
    decodeBoxMap(msg).then((decoded) => {
      latest.current = decoded;
      setMap(decoded);
    }).catch((err) => console.warn("box map: could not decode", err))
      .finally(() => { decoding.current = false; });
  }, { enabled: Boolean(org) });
  return { map, latest, tool, toolLatest };
}
